import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { SampleRecordsService } from '../sample-records.service';
import {
  CreateOrEditSampleRecordComponent,
} from '../create-or-edit-sample-record/create-or-edit-sample-record.component';

/**
 * Compact, DWR-embedded list of sample records scoped to the
 * (daily_report_id, bid_item_material_set_id) tuple. Mirrors the standalone
 * Sample Records tab behavior but without pagination chrome — it lives
 * inline next to the Acceptance Record affordance on a bid-item line.
 *
 * Reuses CreateOrEditSampleRecordComponent for both create + edit by opening
 * it via the same projectsService.openRightPanel pattern the standalone list
 * uses; the only delta is the `embeddedContext` payload which causes the
 * shared form to attach daily_report_id / bid_item_id / bid_item_material_set_id
 * on save.
 */
@Component({
  selector: 'app-embedded-sample-records',
  templateUrl: './embedded-sample-records.component.html',
  styleUrls: ['./embedded-sample-records.component.scss'],
  standalone: false,
})
export class EmbeddedSampleRecordsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;
  @Input() dailyReportId: number | string;
  @Input() bidItemId: number | string;
  @Input() bidItemMaterialSetId: number | string;
  // When set, the list is filtered down to samples whose material_id matches.
  // Used by the dialog flow that renders one Samples block per material under
  // its acceptance-fields card.
  @Input() materialId: number | string | null = null;
  /**
   * Fires just before the sample panel opens. The acceptance-record dialog
   * that hosts this list is a modal above the right panel (overlay z-index
   * 1000 vs the panel's 10), so it closes itself with its current edits on
   * this event — otherwise the panel would open unreachable behind it.
   */
  @Output() panelOpening = new EventEmitter<void>();

  records: any[] = [];
  loading = false;
  loadError: string | null = null;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private sampleRecordsService: SampleRecordsService,
  ) {}

  ngOnInit(): void {
    // Standalone list and the embedded list share one refresh subject —
    // either save/push pulls every visible list back into sync.
    this.sampleRecordsService.refresh
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetch());

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {});

    this.fetch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The DWR may swap material sets while the line item stays mounted; in
    // that case we re-fetch so the list reflects the new scope.
    if (
      (changes['dailyReportId'] && !changes['dailyReportId'].firstChange) ||
      (changes['bidItemMaterialSetId'] && !changes['bidItemMaterialSetId'].firstChange) ||
      (changes['projectId'] && !changes['projectId'].firstChange)
    ) {
      this.fetch();
    }
  }

  private fetch(): void {
    if (!this.projectId || !this.dailyReportId || !this.bidItemMaterialSetId) {
      this.records = [];
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.sampleRecordsService
      .list({
        projectId: this.projectId,
        dailyReportId: this.dailyReportId,
        bidItemMaterialSetId: this.bidItemMaterialSetId,
      })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const all = Array.isArray(resp?.data) ? resp.data : [];
          this.records = this.materialId != null
            ? all.filter((r: any) => String(r.material_id) === String(this.materialId))
            : all;
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.loadError = err?.error?.message || err?.message || 'Failed to load samples';
          this.records = [];
        },
      });
  }

  rowClicked(row: any): void {
    this.panelOpening.emit();
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSampleRecordComponent,
      data: {
        ...row,
        embeddedContext: this.embeddedContext(),
      },
    });
    this.router.navigate([], {
      queryParams: { sampleId: row.uuid },
      queryParamsHandling: 'merge',
    });
  }

  create(): void {
    this.panelOpening.emit();
    this.router.navigate([], {
      queryParams: { sampleId: null },
      queryParamsHandling: 'merge',
    });
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSampleRecordComponent,
      data: { embeddedContext: this.embeddedContext() },
    });
  }

  private embeddedContext() {
    return {
      dailyReportId: this.dailyReportId,
      bidItemId: this.bidItemId,
      bidItemMaterialSetId: this.bidItemMaterialSetId,
      ...(this.materialId != null ? { materialId: this.materialId } : {}),
    };
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
